import React, { useState, useEffect, useRef, useCallback } from 'react';
import './App.css';

const GAME_WIDTH = 800;
const GAME_HEIGHT = 600;
const BALL_RADIUS = 20;
const PLATFORM_WIDTH = 120;
const PLATFORM_HEIGHT = 16;
const PLATFORM_Y = GAME_HEIGHT - 50;
const GRAVITY = 0.6;
const BOUNCE_FORCE = -14;
const PLATFORM_SPEED = 8;
const DIFFICULTY_INTERVAL = 5; // every N points, increase difficulty

function App() {
  const [gameState, setGameState] = useState('start'); // start | playing | over
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(() => {
    return parseInt(localStorage.getItem('ballBounce_highScore') || '0', 10);
  });
  const [scale, setScale] = useState(1);

  const gameRef = useRef(null);
  const wrapperRef = useRef(null);
  const animFrameRef = useRef(null);
  const keysRef = useRef({});
  const scaleRef = useRef(1);

  // Game objects as refs to avoid re-renders in the game loop
  const ballRef = useRef({
    x: GAME_WIDTH / 2,
    y: PLATFORM_Y - BALL_RADIUS - 10,
    vy: 0,
    vx: 0,
  });
  const platformRef = useRef({
    x: GAME_WIDTH / 2 - PLATFORM_WIDTH / 2,
  });
  const scoreRef = useRef(0);
  const difficultyRef = useRef(1);

  // DOM refs for direct manipulation (no React re-render in loop)
  const ballElRef = useRef(null);
  const platformElRef = useRef(null);
  const scoreElRef = useRef(null);
  const comboElRef = useRef(null);

  const resetGame = useCallback(() => {
    // Random initial horizontal velocity so ball doesn't go straight
    const initialVx = (Math.random() - 0.5) * 6;
    ballRef.current = {
      x: GAME_WIDTH / 2,
      y: PLATFORM_Y - BALL_RADIUS - 10,
      vy: BOUNCE_FORCE,
      vx: initialVx,
    };
    platformRef.current = { x: GAME_WIDTH / 2 - PLATFORM_WIDTH / 2 };
    scoreRef.current = 0;
    difficultyRef.current = 1;
    setScore(0);
  }, []);

  const startGame = useCallback(() => {
    resetGame();
    setGameState('playing');
  }, [resetGame]);

  const endGame = useCallback(() => {
    if (animFrameRef.current) {
      cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = null;
    }
    const finalScore = scoreRef.current;
    setScore(finalScore);
    setHighScore((prev) => {
      const newHigh = Math.max(prev, finalScore);
      localStorage.setItem('ballBounce_highScore', String(newHigh));
      return newHigh;
    });
    setGameState('over');
  }, []);

  // Game loop
  useEffect(() => {
    if (gameState !== 'playing') return;

    const loop = () => {
      const ball = ballRef.current;
      const platform = platformRef.current;
      const keys = keysRef.current;
      const difficulty = difficultyRef.current;

      // Platform movement
      const pSpeed = PLATFORM_SPEED + difficulty * 0.5;
      if (keys['ArrowLeft'] || keys['a']) {
        platform.x = Math.max(0, platform.x - pSpeed);
      }
      if (keys['ArrowRight'] || keys['d']) {
        platform.x = Math.min(GAME_WIDTH - PLATFORM_WIDTH, platform.x + pSpeed);
      }

      // Ball physics
      ball.vy += GRAVITY * (1 + difficulty * 0.05);
      ball.y += ball.vy;
      ball.x += ball.vx;

      // Ball horizontal drag
      ball.vx *= 0.99;

      // Wall bounces (left/right)
      if (ball.x - BALL_RADIUS < 0) {
        ball.x = BALL_RADIUS;
        ball.vx = Math.abs(ball.vx) * 0.8;
      }
      if (ball.x + BALL_RADIUS > GAME_WIDTH) {
        ball.x = GAME_WIDTH - BALL_RADIUS;
        ball.vx = -Math.abs(ball.vx) * 0.8;
      }

      // Ceiling bounce
      if (ball.y - BALL_RADIUS < 0) {
        ball.y = BALL_RADIUS;
        ball.vy = Math.abs(ball.vy) * 0.5;
      }

      // Platform collision
      const ballBottom = ball.y + BALL_RADIUS;
      const platformLeft = platform.x;
      const platformRight = platform.x + PLATFORM_WIDTH;
      const platformTop = PLATFORM_Y;

      if (
        ball.vy > 0 &&
        ballBottom >= platformTop &&
        ballBottom <= platformTop + PLATFORM_HEIGHT + ball.vy &&
        ball.x + BALL_RADIUS > platformLeft &&
        ball.x - BALL_RADIUS < platformRight
      ) {
        ball.y = platformTop - BALL_RADIUS;
        ball.vy = BOUNCE_FORCE * (1 + difficulty * 0.03);

        // Add horizontal push based on where ball hits platform + random drift
        const hitPos = (ball.x - platformLeft) / PLATFORM_WIDTH; // 0 to 1
        ball.vx += (hitPos - 0.5) * 5;
        // Add small random perturbation so ball never stays perfectly centered
        ball.vx += (Math.random() - 0.5) * 2;

        // Score
        scoreRef.current += 1;
        if (scoreElRef.current) {
          scoreElRef.current.textContent = scoreRef.current;
        }

        // Difficulty increase
        difficultyRef.current = 1 + Math.floor(scoreRef.current / DIFFICULTY_INTERVAL);

        // Visual feedback
        if (comboElRef.current) {
          comboElRef.current.classList.remove('bounce-flash');
          void comboElRef.current.offsetWidth; // trigger reflow
          comboElRef.current.classList.add('bounce-flash');
        }
      }

      // Lose condition: ball falls below the game area
      if (ball.y - BALL_RADIUS > GAME_HEIGHT) {
        endGame();
        return;
      }

      // Render (direct DOM manipulation for performance)
      if (ballElRef.current) {
        const squash = Math.min(1.3, 1 + Math.abs(ball.vy) * 0.01);
        ballElRef.current.style.left = `${ball.x - BALL_RADIUS}px`;
        ballElRef.current.style.top = `${ball.y - BALL_RADIUS}px`;
        ballElRef.current.style.transform = `scaleX(${2 - squash}) scaleY(${squash})`;
      }
      if (platformElRef.current) {
        platformElRef.current.style.left = `${platform.x}px`;
      }

      animFrameRef.current = requestAnimationFrame(loop);
    };

    animFrameRef.current = requestAnimationFrame(loop);

    return () => {
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current);
      }
    };
  }, [gameState, endGame]);

  // Keyboard input
  useEffect(() => {
    const handleKeyDown = (e) => {
      keysRef.current[e.key] = true;
      if (gameState === 'start' || gameState === 'over') {
        if (e.key === ' ' || e.key === 'Enter') {
          startGame();
        }
      }
    };
    const handleKeyUp = (e) => {
      keysRef.current[e.key] = false;
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [gameState, startGame]);

  // Mouse/touch platform control
  useEffect(() => {
    if (gameState !== 'playing') return;

    const gameEl = gameRef.current;
    if (!gameEl) return;

    const handlePointerMove = (e) => {
      e.preventDefault();
      const rect = gameEl.getBoundingClientRect();
      const currentScale = scaleRef.current;
      const relX = ((e.clientX - rect.left) / (GAME_WIDTH * currentScale)) * GAME_WIDTH;
      platformRef.current.x = Math.max(
        0,
        Math.min(GAME_WIDTH - PLATFORM_WIDTH, relX - PLATFORM_WIDTH / 2)
      );
    };

    gameEl.addEventListener('pointermove', handlePointerMove, { passive: false });
    gameEl.addEventListener('pointerdown', handlePointerMove, { passive: false });

    return () => {
      gameEl.removeEventListener('pointermove', handlePointerMove);
      gameEl.removeEventListener('pointerdown', handlePointerMove);
    };
  }, [gameState]);

  // Viewport scaling — fit game to screen
  useEffect(() => {
    const calculateScale = () => {
      const padding = 16;
      const availW = window.innerWidth - padding;
      const availH = window.innerHeight - padding;
      const s = Math.min(availW / GAME_WIDTH, availH / GAME_HEIGHT, 1);
      setScale(s);
      scaleRef.current = s;
    };

    calculateScale();
    window.addEventListener('resize', calculateScale);
    return () => window.removeEventListener('resize', calculateScale);
  }, []);

  return (
    <div className="app">
      <div className="game-wrapper" ref={wrapperRef} style={{
        width: GAME_WIDTH * scale,
        height: GAME_HEIGHT * scale,
      }}>
        <div
          className="game-area"
          ref={gameRef}
          style={{
            width: GAME_WIDTH,
            height: GAME_HEIGHT,
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
          }}
        >
          {gameState === 'start' && (
            <div className="overlay">
              <h1>Ball Bounce</h1>
              <p>Keep the ball alive by moving the platform!</p>
              <p className="controls-info">
                <strong>Controls:</strong> Arrow keys / A,D / Mouse
              </p>
              <button className="btn" onClick={startGame}>
                Start Game
              </button>
              <p className="hint">or press Space / Enter</p>
              {highScore > 0 && <p className="high-score">Best: {highScore}</p>}
            </div>
          )}

          {gameState === 'over' && (
            <div className="overlay overlay-over">
              <h1>Game Over</h1>
              <p className="final-score">Score: {score}</p>
              {score >= highScore && score > 0 && (
                <p className="new-record">New Record!</p>
              )}
              <p className="high-score">Best: {highScore}</p>
              <button className="btn" onClick={startGame}>
                Play Again
              </button>
              <p className="hint">or press Space / Enter</p>
            </div>
          )}

          {gameState === 'playing' && (
            <>
              <div className="hud">
                <span className="hud-score">
                  Score: <span ref={scoreElRef}>0</span>
                </span>
                <span className="hud-high">Best: {highScore}</span>
                <span className="hud-difficulty" ref={comboElRef}>
                  Level {difficultyRef.current}
                </span>
              </div>
              <div className="ball" ref={ballElRef} />
              <div
                className="platform"
                ref={platformElRef}
                style={{
                  width: PLATFORM_WIDTH,
                  height: PLATFORM_HEIGHT,
                  top: PLATFORM_Y,
                }}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default App;